import React, { useCallback, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCustomT } from "../../hooks/useCustomT";
import styles from "./QuizGameScreen.module.css";
import { ActiveQuizKeyName, getActiveQuiz } from "../../helpers/dataHelpers";
import PlayerScore from "./PlayerScore";
import AnswerDotsPanel from "./AnswerDotsPanel";
import Winner from "./Winner";
import CorrectAnswer from "./CorrectAnswer";
import ExplanationPanelCorrect from "../../components/ExplanationPanel/ExplanationPanelCorrect";
import ExplanationPanelWrong from "../../components/ExplanationPanel/ExplanationPanelWrong";
import { AnswerMemoized, AnswerWithImageMemoized } from "./Answers";
import QuestionPanel from "./QuestionPanel";
import gameConfig from "../../gameConfig";
import { QuizStartScreenUrl } from "../../App";
import {
  getActiveGameKeys,
  getKeyAnswersArrIndex,
  getPlayerNameByKey,
} from "../../helpers/gameHelpers";
import {
  arrayShuffle,
  getColorFromRedToGreenByPercentage,
  shuffle,
} from "../../helpers/commonHelpers";
import withCheckForActiveCategory from "../../HOC/WithCheckForActiveCategory";
import {
  checkIsWithIconsType,
  filterValidQuestions,
  getCorrectAnswer,
  getQuizQuestions,
} from "./helpers/dataHelpers";
import {
  buildImageUrlForQuestionAnswer,
  getImageCaptionForQuestionAnswer,
} from "./helpers/imageHelpers";
import {
  checkAnswerIsAlreadyUsed,
  checkScoreForGameEnd,
} from "./helpers/gamePlayHelpers";

import PointPlateLeftImage from "../../images/pointplate-left.png";
import PointPlateRightImage from "../../images/pointplate-right.png";
import AnswerPictureBlueImage from "../../images/answer-picture-blue.png";
import AnswerPictureYellowImage from "../../images/answer-picture-yellow.png";
import AnswerPictureRedImage from "../../images/answer-picture-red.png";
import AnswerBlueImage from "../../images/answer-blue.png";
import AnswerYellowImage from "../../images/answer-yellow.png";
import AnswerRedImage from "../../images/answer-red.png";
import QuestionTimerImage from "../../images/question-timer.png";
import QuestionTimerRopesImage from "../../images/question-timer-ropes.png";

const PAUSE_BTN_KEY_CODE = 39;
const HIDE_CORRECT_ANSWER_BTN_KEY_CODE = 32;

const activeGameKeys = getActiveGameKeys();

const wrongAnswerPanelTypes = {
  onePlayerWrong: "onePlayerWrong",
  bothPlayersWrong: "bothPlayersWrong",
  timeIsFinished: "timeIsFinished",
};

// To have interval in timer 50ms (with 50 ms animation runs smoothly)
// this dynamically (depends on timer time in the config) coeff is used
const timerTimeToIntervalCoeff = gameConfig.timeForAnswer / 50 / 100;

function QuizGameScreen({ quizzes, sounds }) {
  const [timerPercentage, setTimerPercentage] = useState(100);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isNoMoreQuestionsNextTick, setIsNoMoreQuestionsNextTick] =
    useState(false);
  const [isAnswersWithIcons, setIsAnswersWithIcons] = useState(false);
  const [isGameActive, setIsGameActive] = useState(false);
  const [isGameFinished, setIsGameFinished] = useState(false);
  const [keysPressed, setKeysPressed] = useState([]);
  const [wrongAnswerPanel, setWrongAnswerPanel] = useState({
    isShown: false,
    type: wrongAnswerPanelTypes.onePlayerWrong,
  });
  const [isCorrectAnswerShown, setIsCorrectAnswerShown] = useState(false);
  const [incorrectAnswers, setIncorrectAnswers] = useState([]);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [activePlayers, setActivePlayers] = useState({
    player1: true,
    player2: true,
  });
  const [score, setScore] = useState({
    player1: {
      answers: [],
      score: 0,
    },
    player2: {
      answers: [],
      score: 0,
    },
  });

  const customT = useCustomT();
  const [t, i18n] = useTranslation("common");
  const navigate = useNavigate();

  // Refs
  const scoreRef = React.useRef(null);
  const userMayInteract = React.useRef(true);
  const hideCorrectAnswerTimeoutIdRef = React.useRef(null);
  const timerTimeoutRef = React.useRef(null);
  const isGamePausedRef = React.useRef(false);
  const isCorrectAnswerPausedRef = React.useRef(false);
  const timerIntervalIdRef = React.useRef(null);
  const quizQuestionsRef = React.useRef([]);

  const updateCurrentQuestion = useCallback(
    () => {
      const question = quizQuestionsRef.current[currentQuestionNumber];
      const nextQuestion = quizQuestionsRef.current[currentQuestionNumber + 1];
      if (!nextQuestion) {
        setIsNoMoreQuestionsNextTick(true);
      }
      if (question) {
        question.answers = shuffle(question?.answers);
        setKeysPressed([]);
        setIncorrectAnswers([]);
        setNotActivePlayersByFlag("reset");
        setIsAnswersWithIcons(checkIsWithIconsType(question));
        setTimeout(() => {
          setCorrectAnswer(getCorrectAnswer(question));
        }, 500);
        setCurrentQuestionNumber((prevNumber) => prevNumber + 1);
        setCurrentQuestion(question);
      } else {
        setCurrentQuestion(null);
        setIsGameFinished(true);
      }
    },
    [
      setCurrentQuestion,
      setCurrentQuestionNumber,
      currentQuestionNumber,
      setIsNoMoreQuestionsNextTick,
      setIncorrectAnswers,
    ]
  );
  

  const onCorrectAnswerShowKeydown = (e) => {

    const removePanel = () => {
      if (hideCorrectAnswerTimeoutIdRef.current) {
        clearTimeout(hideCorrectAnswerTimeoutIdRef.current);
        hideCorrectAnswerTimeoutIdRef.current = null;
      }
      hideCorrectAnswer();
    }

    // IF pause button is pressed proceed as pause functionality
    if (e.keyCode === PAUSE_BTN_KEY_CODE) {
      // Pause correct answer for the first pause button press
      if (!isCorrectAnswerPausedRef.current) {
        clearTimeout(hideCorrectAnswerTimeoutIdRef.current);
        hideCorrectAnswerTimeoutIdRef.current = null;
        isCorrectAnswerPausedRef.current = true;
      // On second press remove panel
      } else {
        removePanel();
      }
    // IF any other button pressed
    // Clear timeout if user prefer to skip showing of correct answer screen
    // by click in some key on keypad to avoid doubling of functionality
    } else if (e.keyCode === HIDE_CORRECT_ANSWER_BTN_KEY_CODE) {
      removePanel();
    }
  }

  function hideCorrectAnswer() {
    window.removeEventListener("keydown", onCorrectAnswerShowKeydown);
    setIsCorrectAnswerShown(false);
    const isGameEnded = checkScoreForGameEnd(scoreRef.current);
    setIsGameFinished(isGameEnded);
    updateCurrentQuestion();
    if (isGameEnded) {
      return;
    } else if (!isNoMoreQuestionsNextTick) {
      startTimer();
    } else {
      setTimeout(() => {
        navigate(QuizStartScreenUrl);
      }, 3000);
    }
  }

  const showAnswerIsCorrect = (status, delay) => {
    setIsGameActive(false);
    sounds.timerSoundStop();

    switch (status) {
      case "correct":
        sounds.rightAnswerSound();
        break;
      case "wrong":
        sounds.wrongAnswerSound();
    }
    const showDelay = delay ? delay : gameConfig.showCorrectAnswerDelay;
    window.removeEventListener("keydown", onKeyDown);
    // use some delay (timeout) to show correct answer after sound
    setTimeout(() => {
      setIsCorrectAnswerShown(true);
      resetTimer();
      // use some delay (timeout) to avoid user skip correct answer before see it
      setTimeout(() => {
        // Listen to keydown if user prefer to skip showing of correct answer screen
        // by clicking on some key on a keypad
        window.addEventListener("keydown", onCorrectAnswerShowKeydown);
      }, 500);
    }, showDelay);
    hideCorrectAnswerTimeoutIdRef.current = setTimeout(
      hideCorrectAnswer,
      showDelay + gameConfig.showCorrectAnswerTime
    );
  };

  const showAnswerIsWrong = (
    keyAnswerIndex,
    type = wrongAnswerPanelTypes.onePlayerWrong,
    showCorrectAnswerAfter
  ) => {
    setIsGameActive(false);
    sounds.timerSoundPause();
    sounds.wrongAnswerSound();
    setIncorrectAnswers((prevArr) => [...prevArr, keyAnswerIndex]);
    setTimeout(() => {
      setWrongAnswerPanel({ type: type, isShown: true });
    }, gameConfig.showWrongAnswerDelay);
    timerTimeoutRef.current = setTimeout(() => {
      setWrongAnswerPanel((prevState) => ({
        ...prevState,
        isShown: false,
      }));
      if (!showCorrectAnswerAfter) {
        setTimeout(() => {
          startTimer();
        }, 500);
      } else {
        // Show correct answer after showing wrong answer text
        showAnswerIsCorrect(null, 500);
      }
    }, gameConfig.showWrongAnswerDelay + gameConfig.showWrongAnswerTime);
  };

  const startTimer = useCallback(() => {
    const intervalID = setInterval(() => {
      setTimerPercentage((prevCount) => {
        if (prevCount <= 2) {
          clearInterval(intervalID);
          return 0;
        }
        return prevCount - (1 / timerTimeToIntervalCoeff).toFixed(3);
      });
    }, gameConfig.timeForAnswer / (100 * timerTimeToIntervalCoeff));

    timerIntervalIdRef.current = intervalID;
    setIsGameActive(true);
    sounds.timerSound();
  }, []);

  const stopTimer = (keepGameActive = false) => {
    if (!keepGameActive) {
      setIsGameActive(false);
    }
    clearInterval(timerIntervalIdRef.current);
    timerIntervalIdRef.current = null;
  };

  const resetTimer = () => {
    setTimerPercentage(100);
  };

  const setNotActivePlayersByFlag = (flag = "reset") => {
    switch (flag) {
      case "reset":
        setActivePlayers({
          player1: true,
          player2: true,
        });
        break;
      case gameConfig.playerOneName:
        setActivePlayers((oldState) => ({
          ...oldState,
          player1: false,
        }));
        break;
      case gameConfig.playerTwoName:
        setActivePlayers((oldState) => ({
          ...oldState,
          player2: false,
        }));
        break;
      case "both":
        setActivePlayers({
          player1: false,
          player2: false,
        });
    }
  };

  const onKeyDown = useCallback(
    (e) => {
      if (isGameActive) {
        let keysToCheck = activeGameKeys;
        if (!activePlayers.player1 && !activePlayers.player2) {
          keysToCheck = [];
        } else if (!activePlayers.player2) {
          keysToCheck = gameConfig.keys.keysFirstPlayer;
        } else if (!activePlayers.player1) {
          keysToCheck = gameConfig.keys.keysSecondPlayer;
        }
        if (
          keysToCheck.includes(e.key) &&
          !checkAnswerIsAlreadyUsed(keysPressed, e.key)
        ) {
          // userMayInteract check is used to avoid work with
          // several keys pushed in one time.
          // This ensures that only one button will be processed in one time
          if (userMayInteract.current) {
            sounds.buttonSound();
            setKeysPressed((keysPressed) => {
              let newKeyPressed = keysPressed;
              if (!keysPressed.includes(e.key)) {
                newKeyPressed = [...keysPressed, e.key];
              }
              return newKeyPressed;
            });
            // set to false to avoid double keys click
            userMayInteract.current = false;
          }
        }
      }
    },
    [setKeysPressed, activePlayers, isGameActive]
  );

  const updatePlayerScore = (playerName, answerStatus) => {
    if (playerName) {
      let playerKey =
        playerName === gameConfig.playerOneName ? "player1" : "player2";
      setScore((oldScore) => ({
        ...oldScore,
        [playerKey]: {
          score: answerStatus
            ? oldScore[playerKey].score + 1
            : oldScore[playerKey].score,
          answers: [
            ...oldScore[playerKey].answers,
            {
              status: answerStatus,
            },
          ],
        },
      }));
    }
  };


  // This function is used only for pausing/unpausing game itself
  const handleGamePause = useCallback((e) => {
    if (e.keyCode === PAUSE_BTN_KEY_CODE) {
      if (isGamePausedRef.current) {
        isGamePausedRef.current = false;
        userMayInteract.current = true;
        startTimer();
      } else {
        isGamePausedRef.current = true;
        userMayInteract.current = false;
        stopTimer(true);
        sounds.timerSoundPause();
      }
    }
  }, []);

  // Effects
  useEffect(() => {
    const activeQuiz = getActiveQuiz(quizzes);
    const quizQuestions = getQuizQuestions(activeQuiz);

    // Validate questions. Filter only valid questions
    const validQuizQuestions = filterValidQuestions(quizQuestions);

    if (!validQuizQuestions.length) {
      alert("No valid questions for this category");
      setTimeout(() => {
        localStorage.removeItem(ActiveQuizKeyName);
        navigate("/");
      }, 3000);
    } else {
      // Shuffle questions before each game
      // Question are in ref because they are used in useCallback callbacks
      quizQuestionsRef.current = arrayShuffle(validQuizQuestions);
      updateCurrentQuestion();
      startTimer();
    }
    // Stop timer sound on component unmount
    return () => {
      clearTimeout(timerTimeoutRef.current);
      sounds.timerSoundStop();
      sounds.rightAnswerSoundStop();
      sounds.wrongAnswerSoundStop();
    };
  }, []);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    if (isCorrectAnswerShown) {
      userMayInteract.current = false;
    } else {
      userMayInteract.current = true;
      isCorrectAnswerPausedRef.current = false;
    }
  }, [isCorrectAnswerShown]);

  // Do some logic on time of timer has finished
  // Set game not active, show correct answer, but after "wrong" sound
  // Then go to next question (round)
  useEffect(() => {
    if (timerPercentage === 0) {
      setIsGameActive(false);
      clearInterval(timerIntervalIdRef.current);
      showAnswerIsCorrect("wrong");
    }
  }, [timerPercentage]);

  useEffect(() => {
    // add listener for keypress when game is active (timer is on)
    // and remove it when game is not active (timer is paused or stopped)
    if (isGameActive) {
      userMayInteract.current = true;
      window.addEventListener("keydown", onKeyDown);
      window.addEventListener("keydown", handleGamePause);
    } else {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keydown", handleGamePause);
    }
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keydown", handleGamePause);
    };
  }, [isGameActive]);

  // On player's keys press logic
  // Catch which key was pressed and by which user
  // There are several cases:
  // 1. Correct answer: Stop timer. Stop the music. Make both players inactive.
  // Show correct answer text, update score, go to next question (round)
  // 2. Answer is wrong
  // 2.1. One player wrong: Stop timer. Pause the music. Make game inactive. Update score.
  // Show not correct answer text. Make second player inactive.
  // Then continue timer in current round.
  // 2.2. Both players gave wrong answers: Stop timer. Stop the music. Make both players inactive.
  // Update score. Show not correct answer text. Then show correct answer text.
  // Go to the  next question (round).
  //
  // NOTE: Go to the next round means - check the game for end, make both players active, start timer,
  // set game active, start the music
  useEffect(() => {
    if (keysPressed.length && currentQuestion) {
      const currentPressedKey = keysPressed[keysPressed.length - 1];
      const keyAnswerIndex = getKeyAnswersArrIndex(currentPressedKey);
      if (
        currentQuestion.answers[keyAnswerIndex] &&
        !checkAnswerIsAlreadyUsed(keysPressed, currentPressedKey)
      ) {
        stopTimer();
        const playerName = getPlayerNameByKey(currentPressedKey);
        if (currentQuestion.answers[keyAnswerIndex].correct) {
          updatePlayerScore(playerName, true);
          setNotActivePlayersByFlag("both");
          showAnswerIsCorrect("correct");
        } else {
          // Uncomment to return the logic of counting of not correct
          // answers marked with red lights
          // updatePlayerScore(playerName, false);
          if (activePlayers.player1 && activePlayers.player2) {
            setNotActivePlayersByFlag(playerName);
            showAnswerIsWrong(
              keyAnswerIndex,
              wrongAnswerPanelTypes.onePlayerWrong
            );
          } else {
            setNotActivePlayersByFlag("both");
            showAnswerIsWrong(keyAnswerIndex, "bothPlayersWrong", true);
          }
        }
      }
    }
  }, [keysPressed]);
  return (
    <>
      <div className={styles.wrapper}>
        <div className="container">
          {!isGameFinished ? (
            <>
              {currentQuestion && (
                <div className={styles.gameWrapper}>
                  <div className={styles.gameTopPartWrapper}>
                    <div className={styles.questionWrapper}>
                      <QuestionPanel questionText={currentQuestion.question} />
                      <div className={styles.questionTimer}>
                        <div className={styles.questionTimerBarWrapper}>
                          <div
                            className={styles.questionTimerBar}
                            style={{
                              backgroundColor:
                                getColorFromRedToGreenByPercentage(
                                  timerPercentage
                                ),
                              width:
                                timerPercentage > 2
                                  ? `${timerPercentage}%`
                                  : "2%",
                            }}
                          ></div>
                          <img
                            src={QuestionTimerRopesImage}
                            alt="ropes"
                            className={styles.questionTimerRopes}
                          />
                        </div>
                        <img
                          src={QuestionTimerImage}
                          alt="timer panel"
                          className={styles.questionTimerImg}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.gameBottomPartWrapper}>
                    {isAnswersWithIcons ? (
                      <>
                        <AnswerWithImageMemoized
                          isAnswerDown={
                            incorrectAnswers && incorrectAnswers.includes(0)
                          }
                          panelImage={AnswerPictureBlueImage}
                          answerImage={buildImageUrlForQuestionAnswer(
                            currentQuestion,
                            0
                          )}
                          answerImageAlt={getImageCaptionForQuestionAnswer(
                            currentQuestion,
                            0
                          )}
                          answerText={customT(
                            currentQuestion.answers[0].answer
                          )}
                        />
                        <AnswerWithImageMemoized
                          isAnswerDown={
                            incorrectAnswers && incorrectAnswers.includes(1)
                          }
                          panelImage={AnswerPictureYellowImage}
                          answerImage={buildImageUrlForQuestionAnswer(
                            currentQuestion,
                            1
                          )}
                          answerImageAlt={getImageCaptionForQuestionAnswer(
                            currentQuestion,
                            1
                          )}
                          answerText={customT(
                            currentQuestion.answers[1].answer
                          )}
                        />
                        <AnswerWithImageMemoized
                          isAnswerDown={
                            incorrectAnswers && incorrectAnswers.includes(2)
                          }
                          panelImage={AnswerPictureRedImage}
                          answerImage={buildImageUrlForQuestionAnswer(
                            currentQuestion,
                            2
                          )}
                          answerImageAlt={getImageCaptionForQuestionAnswer(
                            currentQuestion,
                            2
                          )}
                          answerText={customT(
                            currentQuestion.answers[2].answer
                          )}
                        />
                      </>
                    ) : (
                      <>
                        <AnswerMemoized
                          isAnswerDown={
                            incorrectAnswers && incorrectAnswers.includes(0)
                          }
                          panelImage={AnswerBlueImage}
                          answerText={customT(
                            currentQuestion.answers[0].answer
                          )}
                        />
                        <AnswerMemoized
                          isAnswerDown={
                            incorrectAnswers && incorrectAnswers.includes(1)
                          }
                          panelImage={AnswerYellowImage}
                          answerText={customT(
                            currentQuestion.answers[1].answer
                          )}
                        />
                        <AnswerMemoized
                          isAnswerDown={
                            incorrectAnswers && incorrectAnswers.includes(2)
                          }
                          panelImage={AnswerRedImage}
                          answerText={customT(
                            currentQuestion.answers[2].answer
                          )}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <Winner score={score} sounds={sounds} />
          )}
        </div>
        <div className={styles.answerDotsWrapper}>
          <AnswerDotsPanel answers={score.player1.answers} />
          <AnswerDotsPanel answers={score.player2.answers} />
        </div>
        <div className={styles.bottomElementsWrapper}>
          <PlayerScore
            image={PointPlateLeftImage}
            imageAlt="the first player score"
            score={score.player1.score}
            playerNameText={`${t("player")} 1`}
            customStyles={{
              wrapperStyles: styles.scoreLeftWrapper,
              scoreValue: styles.scoreLeftScoreValue,
              playerNameText: styles.scoreLeftPlayerNameText,
            }}
          />
          <PlayerScore
            image={PointPlateRightImage}
            imageAlt="the second player score"
            score={score.player2.score}
            playerNameText={`${t("player")} 2`}
            customStyles={{
              wrapperStyles: styles.scoreRightWrapper,
              scoreValue: styles.scoreRightScoreValue,
              playerNameText: styles.scoreRightPlayerNameText,
            }}
          />
        </div>
        {!isGameFinished && (
          <>
            <ExplanationPanelCorrect isShown={isCorrectAnswerShown}>
              {correctAnswer && (
                <CorrectAnswer
                  isAnswersWithIcons={isAnswersWithIcons}
                  correctAnswerImageUrl={
                    correctAnswer.icon && correctAnswer.icon.imageUrl
                      ? correctAnswer.icon.imageUrl
                      : null
                  }
                  correctAnswerCaption={correctAnswer.caption}
                  correctAnswerDescription={correctAnswer.answer_description}
                  correctAnswerAnswer={correctAnswer.answer}
                />
              )}
            </ExplanationPanelCorrect>

            <ExplanationPanelWrong isShown={wrongAnswerPanel.isShown}>
              <div className={styles.answerPanelContent}>
                {wrongAnswerPanel.type ===
                  wrongAnswerPanelTypes.onePlayerWrong && (
                  <>
                    <p className={styles.answerTopText}>{t("wrong-answer")}</p>
                    <p className={styles.wrongAnswerBottomText}>
                      {t("chance-for-player")}{" "}
                      {!activePlayers.player1 ? "2" : "1"}
                    </p>
                  </>
                )}
                {wrongAnswerPanel.type ===
                  wrongAnswerPanelTypes.bothPlayersWrong && (
                  <>
                    <p className={styles.answerTopText}>
                      {t("both-answers-wrong")}
                    </p>
                    <p className={styles.wrongAnswerBottomText}>
                      {t("instructions-will-follow")}
                    </p>
                  </>
                )}
                {wrongAnswerPanel.type ===
                  wrongAnswerPanelTypes.timeIsFinished && (
                  <>
                    <p className={styles.answerTopText}>
                      {t("time-is-finished")}
                    </p>
                    <p className={styles.wrongAnswerBottomText}>
                      {t("instructions-will-follow")}
                    </p>
                  </>
                )}
              </div>
            </ExplanationPanelWrong>
          </>
        )}
      </div>
    </>
  );
}

export default withCheckForActiveCategory(QuizGameScreen);